import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useMe } from '../lib/queries'
import { useHolidays, useLeave } from '../lib/time'
import { can } from '../lib/types'
import { awayPopulation } from '../lib/whoIsAway'
import { addDays, monthDates, today as todayIso } from '../pages/time/dates'
import { leaveRecordHref } from '../pages/time/leaveLink'
import { LEAVE_ADMIN_ABILITY } from '../pages/time/scopes'
import type { LeaveApplication } from '../pages/time/timeData'
import { useRoster } from '../pages/time/ui'
import { LeaveCalendar } from './LeaveCalendar'

export function WhoIsAway({
  onlyIds,
  rowsVisible,
  onSelect,
}: {
  onlyIds?: ReadonlySet<string>
  rowsVisible?: number
  onSelect?: (app: LeaveApplication) => void
}) {
  const navigate = useNavigate()
  const { data: user } = useMe()
  const { people, isLoading: peopleLoading } = useRoster()
  const [month, setMonth] = useState(() => todayIso())
  const dates = monthDates(month)
  // `from`/`to` key the query, so paging re-fetches only the new month, and the route reads them as
  // overlap (`end_date >= from AND start_date <= to`), so a run that started last month still shows.
  const leave = useLeave('all', { from: dates[0]!, to: dates[dates.length - 1]! })
  useHolidays(addDays(dates[0]!, -40), addDays(dates[dates.length - 1]!, 40))
  const applications = useMemo(() => leave.data ?? [], [leave.data])
  const shown = useMemo(() => awayPopulation(people, onlyIds), [people, onlyIds])
  const leaveLoad = leave.isError ? 'error' : leave.isLoading || peopleLoading ? 'loading' : 'ready'
  return (
    <LeaveCalendar
      people={shown}
      applications={applications}
      month={month}
      onMonthChange={setMonth}
      onSelect={onSelect ?? ((app) => navigate(leaveRecordHref(app.id, can(user, LEAVE_ADMIN_ABILITY))))}
      load={leaveLoad}
      rowsVisible={rowsVisible}
    />
  )
}
